import React, { useState } from "react"
import ToggleChoiceList from "./ToggleChoiceList"
import {
  Choice,
  ChoiceValue,
  ManualInput,
  NestedMultiChoices,
} from "./carAssemblageTypes"
import ManualInputs from "./ManualInputs"
import Chip from "./Chip"
import { trackFunction } from "website_script/lib/iframeTrackingHelper"

function isCustomChoice(
  selected: NonNullable<SingleChoiceProps["selected"]>,
  choices: Choice[]
) {
  return !choices.some((choice) => {
    return Object.keys(selected).every(
      (key) => selected[key] === choice.value[key]
    )
  })
}

type SingleChoiceProps = {
  choices: Choice[]
  allChoices: Choice[]
  manualInputs: ManualInput[]
  nestedMultiChoices?: NestedMultiChoices
  doneCallback: (selected: ChoiceValue) => void
  stepName?: string
  labels: {
    seeAll: string
    close: string
    continue: string
    nestedMultiChoicesPrompt: string
    manualInputContinue: string
    other: string
  }
  selected?: ChoiceValue | null
  isSellYourCar?: boolean
  showConfirmButtonAfterSelection?: boolean
  fullWidthOptions?: boolean
  clearStepSelected?: () => void
}

export type CommonChoiceProps = Pick<
  SingleChoiceProps,
  | "selected"
  | "nestedMultiChoices"
  | "fullWidthOptions"
  | "isSellYourCar"
  | "showConfirmButtonAfterSelection"
  | "stepName"
> & {
  selectItem: SingleChoiceProps["doneCallback"]
}

const SingleChoice = ({
  choices,
  nestedMultiChoices,
  manualInputs = [],
  doneCallback,
  labels,
  selected,
  isSellYourCar,
  showConfirmButtonAfterSelection,
  fullWidthOptions,
  allChoices = [],
  clearStepSelected,
  stepName,
}: SingleChoiceProps) => {
  const [showManualInputs, setShowManualInputs] = useState(
    selected && manualInputs.length
      ? isCustomChoice(selected, choices.concat(allChoices))
      : false
  )

  const defaultManualInputsValues = manualInputs.reduce(
    (choiceValue, input) => {
      choiceValue[input.name] = ""
      return choiceValue
    },
    {} as ChoiceValue
  )

  const handleOtherSelect = () => {
    clearStepSelected?.()
    setShowManualInputs(true)
    trackFunction({
      entity: "car_form",
      action: "clicked",
      element: `other for ${stepName}`,
      flow: "trade_in",
      type: "micro_proceeding",
    })
  }

  const manualInputsValues = selected ?? defaultManualInputsValues

  const showOnlyManualInputs = choices.length === 0 && allChoices.length === 0

  return showOnlyManualInputs ? (
    <ManualInputs
      manualInputs={manualInputs}
      onSubmit={doneCallback}
      values={manualInputsValues}
      labels={{ continue: labels.manualInputContinue }}
      stepName={stepName}
    />
  ) : (
    <>
      <ToggleChoiceList
        stepName={stepName}
        possibleCategorizedChoices={[{ options: choices }]}
        allCategorizedChoices={[{ options: allChoices }]}
        labels={{
          seeAll: labels.seeAll,
          close: labels.close,
          continue: labels.continue,
          nestedMultiChoicesPrompt: labels.nestedMultiChoicesPrompt,
        }}
        nestedMultiChoices={nestedMultiChoices}
        selectItem={doneCallback}
        selected={selected}
        fullWidthOptions={fullWidthOptions}
        isSellYourCar={isSellYourCar}
        showConfirmButtonAfterSelection={showConfirmButtonAfterSelection}
        otherChip={
          manualInputs.length ? (
            <Chip
              label={labels.other}
              value={{ key: "other" }}
              onSelect={handleOtherSelect}
              color={null}
              isSelected={showManualInputs}
            />
          ) : null
        }
      />

      {showManualInputs && (
        <ManualInputs
          manualInputs={manualInputs}
          onSubmit={doneCallback}
          values={manualInputsValues}
          labels={{ continue: labels.manualInputContinue }}
          stepName={stepName}
        />
      )}
    </>
  )
}

export default SingleChoice
